/*
Top-level component. Wires React Router routes and gates them behind
the auth context. The layout stays minimal — header with nav + main
content area.
*/

import { useEffect, useState, type JSX, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api } from "./lib/api";
import { useAuth } from "./lib/auth";
import i18n from "./lib/i18n";

import { LoginPage } from "./pages/LoginPage";
import { CardsPage } from "./pages/CardsPage";
import { CardDetailPage } from "./pages/CardDetailPage";
import { CompareCardsPage } from "./pages/CompareCardsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SharedPage } from "./pages/SharedPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { StatsPage } from "./pages/StatsPage";

/*
Renders the main router. The top-level layout (header + nav) wraps any
page that requires authentication.
*/
export function App(): JSX.Element
{
	const { userId, bootstrapping } = useAuth();	// Auth state
	const { t } = useTranslation("common");

	if (bootstrapping)
	{
		return <div className="muted" style={{ padding: "2rem" }}>{t("loading")}</div>;
	}

	if (userId === null)
	{
		return (
			<Routes>
				<Route path="/login"                            element={<LoginPage />} />
				<Route path="*"                                 element={<Navigate to="/login" replace />} />
			</Routes>
		);
	}

	return (
		<AppLayout>
			<Routes>
				<Route path="/cards"           element={<CardsPage />} />
				<Route path="/cards/compare"   element={<CompareCardsPage />} />
				<Route path="/cards/:id"       element={<CardDetailPage />} />
				<Route path="/shared"          element={<SharedPage />} />
				<Route path="/profile"         element={<ProfilePage />} />
				<Route path="/admin/stats"      element={<AdminGuard><StatsPage /></AdminGuard>} />
				<Route path="/admin/users"      element={<AdminGuard><AdminUsersPage /></AdminGuard>} />
				<Route path="/admin/audit-log"  element={<AdminGuard><AuditLogPage /></AdminGuard>} />
				<Route path="/login"            element={<Navigate to="/cards" replace />} />
				<Route path="*"                 element={<Navigate to="/cards" replace />} />
			</Routes>
		</AppLayout>
	);
}

/*
Layout component shown for every authenticated page. Header carries
navigation + logout; main element receives the page content.
*/
function AppLayout(
	{ children }: { children: ReactNode }
): JSX.Element
{
	const { role, logout }	= useAuth();	// Auth state
	const fNavigate			= useNavigate();// Router navigate
	const { t }				= useTranslation("nav");

	const fHandleLogout = () =>
	{
		logout();
		fNavigate("/login", { replace: true });
	};

	return (
		<div className="app-layout">
			<header className="app-header">
				<div className="app-header__left">
					<img className="app-header__logo" src="/spire-blanche.png" alt="" />
					<h1>{t("app_name")}</h1>
				</div>
				<div className="app-header__right">
					<div className="app-header__meta">
						<span className="app-header__version">v{__APP_COMMIT_COUNT__}</span>
						<span className="app-header__copyright">{t("copyright")}</span>
					</div>
					<LanguageSwitcher />
					<button onClick={fHandleLogout}>{t("logout")}</button>
				</div>
			</header>
			<nav className="app-tabs">
				<NavLink to="/cards" className={({ isActive }) => (isActive ? "active" : "")}>
					{t("my_cards")}
				</NavLink>
				<SharedNavLink />
				<NavLink to="/profile" className={({ isActive }) => (isActive ? "active" : "")}>
					{t("my_account")}
				</NavLink>
				{role === "ADMIN"
					? <NavLink to="/admin/stats" className={({ isActive }) => (isActive ? "active" : "")}>
						{t("statistics")}
					</NavLink>
					: null}
				{role === "ADMIN"
					? <NavLink to="/admin/users" className={({ isActive }) => (isActive ? "active" : "")}>
						{t("users")}
					</NavLink>
					: null}
				{role === "ADMIN"
					? <NavLink to="/admin/audit-log" className={({ isActive }) => (isActive ? "active" : "")}>
						{t("audit")}
					</NavLink>
					: null}
			</nav>
			<main className="app-content">{children}</main>
		</div>
	);
}

/*
Tiny <select>-based language switcher in the header. Persists the
chosen language via i18next's localStorage detector so the pick
survives reloads.
*/
function LanguageSwitcher(): JSX.Element
{
	const [fLang, fSetLang] = useState<string>(i18n.language);

	useEffect(() =>
	{
		const onChange = (l: string) => fSetLang(l);

		i18n.on("languageChanged", onChange);

		return () => i18n.off("languageChanged", onChange);
	}, []);

	return (
		<select
			className="lang-switcher"
			value={fLang.startsWith("en") ? "en" : fLang.startsWith("es") ? "es" : "fr"}
			onChange={(e) => { void i18n.changeLanguage(e.target.value); }}
			aria-label="Language"
		>
			<option value="fr">Français</option>
			<option value="en">English</option>
			<option value="es">Español</option>
		</select>
	);
}

/*
Nav link to /shared with a small red badge showing the count of
unread incoming shares (Sharing rows where viewedAt IS NULL). The
count refreshes on mount and after each navigation event so it
visibly clears once the user opens a shared card. Polling would also
work, but on-navigation refresh is enough for this app's pace and
keeps the network footprint at one extra request per page change.
*/
function SharedNavLink(): JSX.Element
{
	const vLocation	= useLocation();
	const [fCount, fSetCount] = useState<number>(0);

	useEffect(() =>
	{
		api<{ count: number }>("GET", "/contents/shared/unread-count")
			.then((vResp) => fSetCount(vResp.count))
			.catch(() => { /* keep last known count on transient errors */ });
	}, [vLocation.pathname]);

	return (
		<NavLink to="/shared" className={({ isActive }) => (isActive ? "active" : "")}>
			Partagés avec moi
			{fCount > 0
				? <span className="badge badge--unread" style={{ marginLeft: "0.4rem" }}>{fCount}</span>
				: null}
		</NavLink>
	);
}

/*
Renders its children only when the current user is an admin, else
sends the user back to the cards page.
*/
function AdminGuard(
	{ children }: { children: ReactNode }
): JSX.Element
{
	const { role } = useAuth();	// Auth state

	if (role !== "ADMIN")
	{
		return <Navigate to="/cards" replace />;
	}

	return <>{children}</>;
}
